import { makeAutoObservable, runInAction } from "mobx";
import { ANALYTICS_EVENTS } from "../analytics-store.tsx";
import { BriaAxios } from "../config/axios";
import { POSTHOG_EVENTS } from "../constants/PostHogEvents";
import { getSelectedOrganization } from "../helpers/localStorage.ts";
import { IRootStore } from "../mobx/root-store.tsx";
import { InvitedUser } from "../models/Invitation.ts";
import { IAgent } from "../models/agent.ts";
import { ICrmEvent } from "../models/crmEvents.ts";
import { ApiKey, MyAccountUser } from "../models/organization";
import User from "../models/user.ts";
import EventsService from "../services/CrmService.ts";
import ModelsService from "../services/ModelsService.ts";
import postHogService from "../services/PostHogService";
import QueryService from "../utils/QueryService.ts";

export interface IAppStore {
	isLoading: boolean;
	isSendRequestAccessLoading: boolean;
	isUserDeletionLoading: boolean;
	isPopupLoading: boolean;
	isQuoteLoading: boolean;
	isVerifyingAgentInstallation: boolean;
	apiKeys: ApiKey[];
	isError: boolean;
	invitedUser: InvitedUser | null;
	ancestorsUsers: MyAccountUser[];
	myAccountUsers: MyAccountUser[];
	agentInstallation: IAgent | null;
	dismissWarningAgentMessage: boolean;
	isSendSuggestMethodLoading: boolean;
	isSendFeedbackLoading: boolean;
	isRegisteringPluginId: boolean;

	loadAncestorsUsers(): Promise<void>;

	grantUsersPermission(selectedUsers: string[], role: string): Promise<void>;

	loadMyAccountUsers(): Promise<void>;

	sendUserInvitations(emails: string[]): Promise<boolean>;

	logPhotoshopGoToAdobeMarketplace(): Promise<void>;

	registerPluginId(pluginAuthId: string, error: string): Promise<boolean>;

	sendRequestAccess(
		userName: string,
		userEmail: string,
		orgName: string,
		message: string,
		phoneNumber: string,
		requestFeature: string,
		modelId?: string,
		modelName?: string,
		projectId?: string,
		projectName?: string,
	): Promise<boolean>;

	getUserInvitation(token: string): Promise<void>;

	acceptUserInvitation(): Promise<void>;

	updateMyAccountUser(usersToUpdate: any[]): Promise<void>;

	deleteMyAccountUser(user_uid: string): Promise<void>;

	deleteUser(): Promise<any>;

	validateUserDeletion(): Promise<any>;

	loadApiKeys(): Promise<void>;

	getComfyUIAPIToken(): Promise<string>;

	createApiKey(keyType: string): Promise<void>;

	updateApiKey(id: string, status: string): Promise<void>;

	logCrmEvents(crmEvents: ICrmEvent[]): Promise<void>;

	verifyAgentInstallation(): Promise<void>;

	deleteUserAndOrganization(): Promise<void>;

	updateUserInfo(user: User, user_name: string, profile_picture?: string, userRole?: string): Promise<void>;

	suggestAccessMethod(suggestion: string): Promise<void>;

	shareFeedback(feedback: string): Promise<void>;

	reset: () => void;
	sendCommonRequestAccess(data: any): Promise<boolean>;
}

export default class AppStore implements IAppStore {
	rootStore: IRootStore;
	apiKeys: ApiKey[] = [];
	ancestorsUsers: MyAccountUser[] = [];
	myAccountUsers: MyAccountUser[] = [];
	isLoading: boolean = false;
	isError: boolean = false;
	isRegisteringPluginId: boolean = false;
	isVerifyingAgentInstallation: boolean = false;
	invitedUser: InvitedUser | null = null;
	isPopupLoading: boolean = false;
	isSendRequestAccessLoading: boolean = false;
	isUserDeletionLoading: boolean = false;
	isQuoteLoading: boolean = false;
	eventsService = new EventsService();
	modelsService = new ModelsService();
	agentInstallation: IAgent | null = null;
	dismissWarningAgentMessage: boolean = false;
	isSendSuggestMethodLoading: boolean = false;
	isSendFeedbackLoading: boolean = false;
	private pluginsQueryService: QueryService = new QueryService("/plugins");
	private suggestMethodQueryService: QueryService = new QueryService("/");

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	loadMyAccountUsers = async (): Promise<any> => {
		this.isLoading = true;
		this.isError = false;
		try {
			(await BriaAxios()).get(`/my_account_users/`).then((res) => {
				runInAction(() => {
					if (res.data.length === 0) {
						this.isError = true;
					} else {
						this.myAccountUsers = res.data;
						this.isError = false;
					}
					this.isLoading = false;
				});
				return Promise.resolve(res.data);
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	loadAncestorsUsers = async (): Promise<any> => {
		try {
			this.isPopupLoading = true;
			const response = await (await BriaAxios()).get(`/my_account_users/ancestors/`);
			runInAction(() => {
				this.ancestorsUsers = response.data;
				this.isPopupLoading = false;
			});
		} catch (e) {
			this.isPopupLoading = false;
			this.isError = true;
			return Promise.reject(e);
		}
	};

	grantUsersPermission = async (selectedUsers: string[], role: string): Promise<void> => {
		try {
			this.isLoading = true;
			const data = {
				users: selectedUsers,
				role: role,
			};
			const response = await (await BriaAxios()).post(`/my_account_users/grant_permission/`, data);
			runInAction(() => {
				if (response.data.length > 0) {
					this.myAccountUsers = [...this.myAccountUsers, ...response.data];
				}
				this.isLoading = false;
				this.isError = false;
				this.ancestorsUsers = [];
				return Promise.resolve(true);
			});
			return Promise.resolve(response.data);
		} catch (e) {
			this.isPopupLoading = false;
			this.isError = true;
			return Promise.reject(e);
		}
	};

	updateMyAccountUser = async (usersToUpdate: any[]): Promise<any> => {
		this.isLoading = true;
		this.isError = false;
		try {
			const response = await (await BriaAxios()).post(`/my_account_user/update/`, usersToUpdate);
			runInAction(() => {
				this.myAccountUsers = [
					...this.myAccountUsers.map((user: MyAccountUser) => {
						if (user.uid === response.data.uid) {
							return response.data;
						}
						return user;
					}),
				];
				this.isLoading = false;
				this.isError = false;
				return Promise.resolve(true);
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	deleteMyAccountUser = async (user_uid: string): Promise<any> => {
		this.isLoading = true;
		this.isError = false;
		try {
			const data = {
				uid: user_uid,
			};
			(await BriaAxios()).post(`/my_account_user/delete/`, data).then((res) => {
				runInAction(() => {
					this.myAccountUsers = this.myAccountUsers.filter((user) => user.uid !== res.data.uid);

					this.isLoading = false;
					this.isError = false;
					return Promise.resolve(true);
				});
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	deleteUser = async (): Promise<any> => {
		this.isLoading = true;
		this.isError = false;
		try {
			const response = await (await BriaAxios()).delete(`/users/`);
			if (response.status === 200) {
				return Promise.resolve(response.data);
			}
		} catch (e) {
			return Promise.reject(e);
		}
	};

	validateUserDeletion = async (): Promise<any> => {
		try {
			const response = await (await BriaAxios()).get(`/users/validate_deletion/`);
			if (response.status === 200) {
				return Promise.resolve(response.data);
			}
		} catch (e) {
			return Promise.reject(e);
		}
	};

	async sendUserInvitations(emails: string[], orgID: string = ""): Promise<boolean> {
		this.isError = false;
		this.isPopupLoading = true;
		const data = {
			emails: emails,
		};
		try {
			let response;
			const selectedOrg = getSelectedOrganization();

			if (orgID) {
				response = await (await BriaAxios()).post(`/organizations/${orgID}/invite_users`, data);

				// Track user invitations to sub-organization
				emails.forEach((email) => {
					postHogService.trackEvent(
						POSTHOG_EVENTS.USER_INVITED_TO_ORG,
						{
							invited_user_email: email.trim(),
							org_id: orgID, // Sub-organization ID
							parent_org_id: selectedOrg?.organization.uid,
							parent_org_name: selectedOrg?.organization.name,
							role: "User",
						},
						this.rootStore.authStore.user,
					);
				});
			} else {
				response = await (await BriaAxios()).post(`/my_account_user/invitation/`, data);

				// Track user invitations to current organization
				emails.forEach((email) => {
					postHogService.trackEvent(
						POSTHOG_EVENTS.USER_INVITED_TO_ORG,
						{
							invited_user_email: email.trim(),
							role: "User",
						},
						this.rootStore.authStore.user,
					);
				});
			}
			runInAction(() => {
				this.isError = false;
				this.isPopupLoading = false;
			});
			return response.status === 200;
		} catch (e) {
			runInAction(() => {
				this.isPopupLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async sendRequestAccess(
		userName: string,
		userEmail: string,
		orgName: string,
		message: string,
		phoneNumber: string,
		requestFeature: string,
		modelId?: string,
		modelName?: string,
		projectId?: string,
		projectName?: string,
	): Promise<boolean> {
		this.isError = false;
		this.isSendRequestAccessLoading = true;
		const data = {
			userName: userName,
			userEmail: userEmail,
			orgName: orgName,
			message: message,
			phoneNumber: phoneNumber,
			requestFeature: requestFeature,
			modelId: modelId,
			modelName: modelName,
			projectId: projectId,
			projectName: projectName,
		};
		try {
			const response = await (await BriaAxios()).post(`/request_access/`, data);
			runInAction(() => {
				this.isError = false;
				this.isSendRequestAccessLoading = false;
			});
			return response.status === 200;
		} catch (e) {
			runInAction(() => {
				this.isSendRequestAccessLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async sendCommonRequestAccess(data: any): Promise<boolean> {
		this.isError = false;
		this.isSendRequestAccessLoading = true;
		try {
			const response = await (await BriaAxios()).post(`/common_request_access/`, data);
			runInAction(() => {
				this.isError = false;
				this.isSendRequestAccessLoading = false;
			});
			return response.status === 200;
		} catch (e) {
			runInAction(() => {
				this.isSendRequestAccessLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async suggestAccessMethod(suggestion: string): Promise<void> {
		this.isSendSuggestMethodLoading = true;
		try {
			await this.suggestMethodQueryService.post("suggest_access_method", {
				suggestion,
			});
			runInAction(() => {
				this.isSendSuggestMethodLoading = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isSendSuggestMethodLoading = false;
			});
			return Promise.reject(e);
		}
	}

	async shareFeedback(feedback: string): Promise<void> {
		this.isSendFeedbackLoading = true;
		try {
			await this.suggestMethodQueryService.post("share_feedback", {
				feedback,
			});
			runInAction(() => {
				this.isSendFeedbackLoading = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isSendFeedbackLoading = false;
			});
			return Promise.reject(e);
		}
	}

	async getUserInvitation(token: string | undefined): Promise<void> {
		this.isError = false;
		try {
			const response = await (await BriaAxios()).get(`/my_account_user/invitation/${token}/`);
			runInAction(() => {
				if (response.status === 200 && response?.data?.org_name !== undefined) {
					this.invitedUser = response?.data;
				} else {
					this.isError = true;
				}
				this.isLoading = false;
				if (this.invitedUser) {
					this.invitedUser.flow_step = 1;
				}
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async acceptUserInvitation(): Promise<void> {
		try {
			await (await BriaAxios()).post(`/my_account_user/invitation/${this.invitedUser?.token}/`, {});
			runInAction(() => {
				this.isError = false;
				this.isLoading = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async loadApiKeys(): Promise<any> {
		this.isLoading = true;
		this.isError = false;
		try {
			await (await BriaAxios()).get("/api_keys/").then((res) => {
				runInAction(() => {
					this.apiKeys = res.data;
					this.isLoading = false;
					this.isError = false;
				});
				return Promise.resolve(res.data);
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async createApiKey(keyType: string): Promise<any> {
		this.isLoading = true;
		this.isError = false;
		try {
			await (await BriaAxios()).post(`/create_api_key/`, { key_type: keyType }).then((res) => {
				runInAction(() => {
					this.apiKeys = [...this.apiKeys, res.data];
					this.isLoading = false;
					this.isError = false;

					// Track API key generated
					postHogService.trackEvent(
						POSTHOG_EVENTS.API_KEY_GENERATED,
						{
							feature: this.mapKeyTypeToFeature(keyType),
						},
						this.rootStore.authStore.user,
					);
				});
				return Promise.resolve(true);
			});
		} catch (e: any) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;

				// Track API key generation failed
				postHogService.trackEvent(
					POSTHOG_EVENTS.API_KEY_GENERATION_FAILED,
					{
						error: e?.message || e?.toString() || "Unknown error",
					},
					this.rootStore.authStore.user,
				);
			});
			return Promise.reject(e);
		}
	}

	// Helper method to map key types to feature names
	private mapKeyTypeToFeature = (keyType: string): string => {
		const featureMap: { [key: string]: string } = {
			production: "Production API",
			staging: "Staging API",
			comfy: "ComfyUI",
			mcp: "Model Context Protocol",
		};

		return featureMap[keyType?.toLowerCase()] || keyType || "unknown";
	};

	getComfyUIAPIToken = async (): Promise<string> => {
		try {
			if (this.apiKeys.length === 0) {
				await this.loadApiKeys();
			}
			let comfyUIAPIToken = this.apiKeys.find((key) => key.key_type === "comfy" && key.status === "active")
				?.api_token;

			if (!comfyUIAPIToken) {
				await this.createApiKey("comfy");
				comfyUIAPIToken = this.apiKeys.find((key) => key.key_type === "comfy" && key.status === "active")
					?.api_token;
			}
			return Promise.resolve(comfyUIAPIToken || "");
		} catch (e) {
			return Promise.reject(e);
		}
	};

	async updateApiKey(api_token: string, status: string): Promise<any> {
		this.isLoading = true;
		this.isError = false;
		const data = {
			api_token: api_token,
			status: status,
		};
		try {
			await (await BriaAxios()).post(`/update_api_key/`, data).then((res) => {
				runInAction(() => {
					this.apiKeys = [
						...this.apiKeys.map((key: ApiKey) => {
							if (key.api_token === res.data.api_token) {
								return res.data;
							}
							return key;
						}),
					];
					this.isLoading = false;
					this.isError = false;
					return Promise.resolve(true);
				});
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	async logCrmEvents(crmEvents: ICrmEvent[]): Promise<void> {
		this.isError = false;
		this.isLoading = false;

		try {
			await this.eventsService.logEvents(crmEvents);

			runInAction(() => {
				this.isLoading = false;
				this.isError = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	}

	verifyAgentInstallation = async () => {
		this.isError = false;
		this.isVerifyingAgentInstallation = true;
		try {
			const response = await this.modelsService.verifyAgentInstallation();
			runInAction(() => {
				console.log(response);
				this.agentInstallation = response;
				this.isError = false;
				this.isVerifyingAgentInstallation = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isVerifyingAgentInstallation = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	deleteUserAndOrganization = async (): Promise<void> => {
		this.isUserDeletionLoading = true;
		this.isError = false;
		try {
			const response = await (await BriaAxios()).post(`/delete_organization/`);
			if (response.status === 200) {
				runInAction(() => {
					this.isUserDeletionLoading = false;
					this.isError = false;
				});
				return Promise.resolve();
			} else {
				throw new Error("Failed to delete organization");
			}
		} catch (error) {
			console.error("Error deleting organization:", error);
			runInAction(() => {
				this.isUserDeletionLoading = false;
				this.isError = true;
			});
			return Promise.reject(error);
		}
	};

	updateUserInfo = async (
		user: User,
		user_name: string,
		profile_picture?: string,
		userRole?: string,
	): Promise<void> => {
		runInAction(() => {
			this.isError = false;
		});
		try {
			const data = {
				userRole: userRole ? userRole : user.userRole ? user.userRole : "Other",
				user_name,
				profile_picture: profile_picture ?? user.profilePicture,
			};
			const response = await (await BriaAxios()).put(`/update_user_info`, data);
			if (response.status === 200) {
				runInAction(() => {
					this.isError = false;
				});
				return Promise.resolve();
			} else {
				throw new Error("Failed to update user info");
			}
		} catch (error) {
			console.error("Error updating user info:", error);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(error);
		}
	};

	logPhotoshopGoToAdobeMarketplace = async (): Promise<void> => {
		this.rootStore.analyticsStore.logEvent(ANALYTICS_EVENTS.GO_TO_ADOBE_MARKETPLACE);
	};

	registerPluginId = async (pluginAuthId: string, error: string): Promise<boolean> => {
		try {
			this.isError = false;
			runInAction(() => {
				this.isRegisteringPluginId = true;
			});
			await this.pluginsQueryService.post("/auth/register_plugin_id", {
				plugin_auth_id: pluginAuthId,
				error: error,
			});
			return true;
		} catch (error) {
			console.error("Error registering new plugin id: ", error);

			runInAction(() => {
				this.isError = true;
			});
			return false;
		} finally {
			runInAction(() => {
				this.isRegisteringPluginId = false;
			});
		}
	};

	reset = () => {
		this.isLoading = false;
		this.isSendRequestAccessLoading = false;
		this.isUserDeletionLoading = false;
		this.isPopupLoading = false;
		this.isQuoteLoading = false;
		this.isVerifyingAgentInstallation = false;
		this.isError = false;
		this.isRegisteringPluginId = false;
		this.isSendSuggestMethodLoading = false;
		this.isSendFeedbackLoading = false;
		this.apiKeys = [];
		this.invitedUser = null;
		this.ancestorsUsers = [];
		this.myAccountUsers = [];
		this.agentInstallation = null;
		this.dismissWarningAgentMessage = false;
	};
}
